import React, { useState, useEffect, useRef, useCallback, memo } from "react";
import { Autocomplete, TextField, CircularProgress, Box, Typography, Paper, createFilterOptions, Chip } from "@mui/material";
import axiosInstance from "../../api/axiosInstance";
import { toast } from "react-toastify";

const filter = createFilterOptions();

/**
 * AsyncLabelAutocomplete
 * 
 * Fetches labels from API with pagination, search, and infinite scroll.
 * Allows adding a new label if it doesn't exist.
 */
const AsyncLabelAutocomplete = React.forwardRef((props, ref) => {
    const {
        value,
        onChange,
        error = "",
        label = "Labels",
        placeholder = "Select Labels",
        fullWidth = true,
        size = "small",
        multiple = true,
        ...rest
    } = props;

    // Track latest value prop to prevent stale closures during async operations
    const valueRef = useRef(value);
    useEffect(() => {
        valueRef.current = value;
    }, [value]);

    const [pendingTags, setPendingTags] = useState([]);
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    // Default limit (high enough to create a scrollbar for infinite scroll)
    const limit = 10;

    // To prevent concurrent fetches
    const isFetchingRef = useRef(false);
    // To prevent redundant fetches on re-open
    const lastFetchedSearchRef = useRef(null);

    // Initial value integration - ensure selected options are in the options list
    useEffect(() => {
        if (value) {
            const valuesArr = Array.isArray(value) ? value : [value];
            setOptions((prev) => {
                let newOptions = [...prev];
                valuesArr.forEach(val => {
                    if (val && val.value && !newOptions.find((o) => o.value === val.value)) {
                        newOptions = [val, ...newOptions];
                    }
                });
                return newOptions;
            });
        }
    }, [value]);

    const fetchLabels = useCallback(async (searchStr, pageNum, append = false) => {
        if (isFetchingRef.current) return;

        isFetchingRef.current = true;
        setLoading(true);
        try {
            const params = {
                page: pageNum,
                limit,
                search: searchStr || undefined
            };

            // Track what was successfully requested
            if (pageNum === 1) {
                lastFetchedSearchRef.current = searchStr;
            }

            const response = await axiosInstance.get("api/labels", { params });
            const result = response.data;
            const data = result?.data || result;

            let fetchedItems = [];
            if (Array.isArray(data)) {
                fetchedItems = data;
            } else if (data && typeof data === "object") {
                fetchedItems = Array.isArray(data.items) ? data.items
                    : Array.isArray(data.rows) ? data.rows
                        : Array.isArray(data.results) ? data.results
                            : [];
            }

            const mappedOptions = fetchedItems.map(p => ({
                value: String(p.id || p.Id || ""),
                label: p.name || p.labelName || `Label #${p.id || p.Id || "?"}`,
                original: p
            }));

            setOptions(prev => {
                if (append) {
                    const existingIds = new Set(prev.map(o => o.value));
                    const newOptions = mappedOptions.filter(o => !existingIds.has(o.value));
                    return [...prev, ...newOptions];
                }

                // If not appending, replace options (but keep the selected values if present)
                let newSet = mappedOptions;
                if (value) {
                    const valuesArr = Array.isArray(value) ? value : [value];
                    valuesArr.forEach(val => {
                        if (val && val.value && !newSet.some(o => o.value === val.value)) {
                            newSet = [val, ...newSet];
                        }
                    });
                }
                return newSet;
            });

            setHasMore(mappedOptions.length === limit);
            setPage(pageNum);
        } catch (error) {
            console.error("Failed to fetch labels for autocomplete:", error);
        } finally {
            setLoading(false);
            isFetchingRef.current = false;
        }
    }, [limit, value]);

    // Handle debounced search and initial load on open
    useEffect(() => {
        const timer = setTimeout(() => {
            if (open && inputValue !== lastFetchedSearchRef.current) {
                fetchLabels(inputValue, 1, false);
            }
        }, 300); // 300ms debounce

        return () => clearTimeout(timer);
    }, [inputValue, open, fetchLabels]);

    const handleCreateLabel = async (name) => {
        setCreating(true);
        try {
            const response = await axiosInstance.post("api/labels", { name });
            const newLabel = response.data?.data || response.data;
            const newOption = {
                value: String(newLabel.id || newLabel.Id || ""),
                label: newLabel.name || name,
                original: newLabel
            };

            setOptions(prev => [newOption, ...prev]);
            return newOption;
        } catch (err) {
            console.error("Error creating label:", err);
            const msg = err.response?.data?.message || err.message || "Failed to create label";
            toast.error(msg);
            return null;
        } finally {
            setCreating(false);
        }
    };

    const handleChange = async (event, newValue, reason) => {
        if (reason === 'selectOption' || reason === 'createOption') {
            if (multiple) {
                const latestOption = newValue[newValue.length - 1];
                const isString = typeof latestOption === 'string';

                if (isString || (latestOption && latestOption.isNew)) {
                    const labelName = isString ? latestOption : latestOption.inputValue;

                    // Prevent empty or whitespace-only labels
                    if (!labelName?.trim()) return;

                    // Prevent duplicate label names
                    const currentValue = valueRef.current || [];
                    const isDuplicate = currentValue.some(
                        (l) => (l.label || "").toLowerCase() === labelName.trim().toLowerCase()
                    );
                    if (isDuplicate) return;

                    // Optimistic update: show pending tag
                    const tempId = "temp-" + Date.now();
                    const tempOption = {
                        value: tempId,
                        label: labelName,
                        isTemp: true,
                        original: { color: "#cbd5e1" } // Grey out pending tags
                    };
                    setPendingTags(prev => [...prev, tempOption]);

                    // Fire API
                    const createdOption = await handleCreateLabel(labelName);

                    // Remove pending tag
                    setPendingTags(prev => prev.filter(t => t.value !== tempId));

                    if (createdOption) {
                        const currentParentValue = valueRef.current || [];
                        onChange?.([...currentParentValue, createdOption]);
                    }
                    return;
                }
            } else {
                const isString = typeof newValue === 'string';
                if (isString || (newValue && newValue.isNew)) {
                    const labelName = isString ? newValue : newValue.inputValue;
                    if (!labelName?.trim()) return;

                    const createdOption = await handleCreateLabel(labelName);
                    if (createdOption) {
                        onChange?.(createdOption);
                    } else {
                        onChange?.(null);
                    }
                    return;
                }
            }
        }
        onChange?.(newValue);
    };

    return (
        <Autocomplete
            multiple={multiple}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            isOptionEqualToValue={(option, val) => option?.value === val?.value}
            getOptionLabel={(option) => {
                // e.g value selected with free solo
                if (typeof option === 'string') {
                    return option;
                }
                if (option.inputValue) {
                    return option.inputValue;
                }
                return option?.label || "";
            }}
            options={options}
            freeSolo={true}
            loading={loading && page === 1}
            value={multiple ? (value || []) : (value || null)}
            onChange={handleChange}
            onInputChange={(_, newInputValue, reason) => {
                if (reason === 'input' || reason === 'clear') {
                    setInputValue(newInputValue);
                }
            }}
            clearOnBlur
            selectOnFocus
            size={size}
            fullWidth={fullWidth}
            filterOptions={(opts, params) => {
                const { inputValue } = params;
                const currentValue = multiple ? (value || []) : (value ? [value] : []);
                const selectedValues = new Set(currentValue.map((v) => v.value));
                const selectedLabels = currentValue.map((v) => (v.label || "").toLowerCase());

                // Hide already-selected options from the dropdown
                const unselected = opts.filter((o) => !selectedValues.has(o.value));
                const filtered = filter(unselected, params);

                // Suggest creation only when the typed name isn't already selected
                const isAlreadySelected = selectedLabels.includes(inputValue.trim().toLowerCase());
                const isExisting = opts.some((option) => inputValue.toLowerCase() === option.label.toLowerCase());

                if (inputValue !== '' && !isExisting && !isAlreadySelected) {
                    filtered.push({
                        inputValue,
                        label: `Add "${inputValue}"`,
                        isNew: true,
                    });
                }

                return filtered;
            }}
            ListboxProps={{
                onScroll: (event) => {
                    const list = event.currentTarget;
                    if (list.scrollTop + list.clientHeight >= list.scrollHeight - 10 && hasMore && !loading) {
                        fetchLabels(inputValue, page + 1, true);
                    }
                },
                style: { fontSize: "12px", maxHeight: "160px", overflowY: "auto" }
            }}
            PaperComponent={(paperProps) => (
                <Paper {...paperProps}>
                    {paperProps.children}
                    {loading && page > 1 && (
                        <Box sx={{ textAlign: 'center', py: 0.5, borderTop: '1px solid #f0f0f0' }}>
                            <CircularProgress size={14} color="inherit" />
                        </Box>
                    )}
                </Paper>
            )}
            renderTags={(tagValue, getTagProps) => {
                const allTags = multiple ? [...tagValue, ...pendingTags] : tagValue;
                return allTags.map((option, index) => {
                    const isPending = option.isTemp;
                    return (
                        <Chip
                            {...getTagProps({ index })}
                            key={option.value || option}
                            label={typeof option === 'string' ? option : option.label}
                            size="small"
                            sx={{
                                backgroundColor: option.original?.color ? `${option.original.color}22` : '#f1f5f9',
                                color: option.original?.color || '#64748b',
                                border: 'none',
                                borderRadius: '20px',
                                fontWeight: 400,
                                fontSize: '11px',
                                height: '22px',
                                px: 0.5,
                                opacity: isPending ? 0.6 : 1,
                                borderStyle: isPending ? 'dashed' : 'solid',
                                borderWidth: '1px',
                                '& .MuiChip-deleteIcon': {
                                    color: option.original?.color || '#64748b',
                                    opacity: 0.7,
                                    '&:hover': { opacity: 1 }
                                }
                            }}
                        />
                    );
                });
            }}
            sx={{
                width: "100%",
                ...rest.sx
            }}
            {...rest}
            renderInput={(params) => (
                <TextField
                    {...params}
                    inputRef={ref}
                    label={label}
                    placeholder={placeholder}
                    error={Boolean(error)}
                    helperText={error || ""}
                    variant="outlined"
                    size={size}
                    fullWidth
                    sx={{
                        '& .MuiInputBase-root': {
                            fontSize: '12px',
                            borderRadius: '8px',
                            backgroundColor: '#f8fafc',
                        },
                    }}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <React.Fragment>
                                {(loading || creating) ? <CircularProgress color="inherit" size={16} /> : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                />
            )}
            renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                    <Box component="li" key={option.isNew ? `new-${option.inputValue}` : option.value} {...optionProps}>
                        <Typography
                            variant="body2"
                            sx={{
                                fontSize: '12px',
                                color: option.original?.color || 'inherit',
                                fontWeight: 400,
                            }}
                        >
                            {option.label}
                        </Typography>
                    </Box>
                );
            }}
        />
    );
});

export default memo(AsyncLabelAutocomplete);
